// src/components/dashboard/ClinicalReport.jsx
import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Chip,
  TextField,
  Select,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
} from "@mui/material";
import MedicalServicesOutlinedIcon from "@mui/icons-material/MedicalServicesOutlined";
import SearchIcon from "@mui/icons-material/Search";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";

const ClinicalReport = () => {
  const [isDark, setIsDark] = useState(false);

  useEffect(() => {
    const checkTheme = () => {
      setIsDark(
        document.documentElement.classList.contains("dark-theme") ||
        document.body.classList.contains("dark-theme")
      );
    };
    checkTheme();
    const obs = new MutationObserver(checkTheme);
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    return () => obs.disconnect();
  }, []);

  // Filter States
  const [patient, setPatient] = useState("");
  const [staff, setStaff] = useState("");
  const [encounterType, setEncounterType] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  // Pagination & Data States
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const clinicalRows = [
    { id: "ENC-501", patient: "Eleanor Vance", staff: "Dr. Usman Rehman", type: "Neurology Evaluation", date: "09/15/2026", status: "Completed" },
    { id: "ENC-502", patient: "Charlotte Bennett", staff: "Elena Rostova, RN", type: "Postpartum Care", date: "09/15/2026", status: "Completed" },
    { id: "ENC-503", patient: "Marcus Aurelius Vance", staff: "Dr. Sarah Jenkins", type: "Rehab Follow-up", date: "09/14/2026", status: "In-Progress" },
    { id: "ENC-504", patient: "Sophia Martinez", staff: "Dr. Usman Rehman", type: "EEG Screening", date: "09/13/2026", status: "Completed" },
    { id: "ENC-505", patient: "James Sterling", staff: "Dr. Bilal", type: "Routine Checkup", date: "09/12/2026", status: "Pending" },
  ];

  const tealMain = "#1E5E5F";
  const tealButton = "#245E65";
  const labelTeal = "#276A6C";
  const pastelTealBg = "#D9EBEF";
  const cardBg = isDark ? "#0F172A" : "#FFFFFF";
  const cardBorder = isDark ? "rgba(255, 255, 255, 0.08)" : "#245E65";
  const softCardBorder = isDark ? "rgba(255, 255, 255, 0.08)" : "#EAEFF4";
  const primaryText = isDark ? "#F8FAFC" : "#0A2540";
  const secondaryText = isDark ? "#94A3B8" : "#64748B";
  const controlBg = isDark ? "rgba(255, 255, 255, 0.03)" : "#FFFFFF";
  const controlBorder = isDark ? "rgba(255, 255, 255, 0.12)" : "#CBD5E1";
  const dropdownBg = isDark ? "#1E293B" : "#FFFFFF";

  const handleClear = () => {
    setPatient("");
    setStaff("");
    setEncounterType("");
    setDateFrom("");
    setDateTo("");
  };

  const filteredRows = clinicalRows.filter((r) => {
    return (
      (patient === "" || r.patient.toLowerCase().includes(patient.toLowerCase())) &&
      (staff === "" || r.staff === staff) &&
      (encounterType === "" || r.type === encounterType)
    );
  });

  const menuProps = {
    PaperProps: {
      sx: {
        backgroundColor: dropdownBg,
        border: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "#B8D6DD"}`,
        borderRadius: "8px",
        color: primaryText,
        boxShadow: isDark ? "0 12px 32px rgba(0, 0, 0, 0.6)" : "0 8px 24px rgba(15, 23, 42, 0.08)",
        "& .MuiMenuItem-root": {
          fontSize: "0.82rem",
          py: 0.8,
          "&:hover": { backgroundColor: isDark ? "rgba(255, 255, 255, 0.06)" : pastelTealBg },
          "&.Mui-selected": { backgroundColor: isDark ? "rgba(30, 94, 95, 0.35)" : pastelTealBg, color: tealButton, fontWeight: 700 },
        },
      },
    },
  };

  return (
    <Box sx={{ width: "100%", pb: 6 }}>
      {/* 1. Header Banner */}
      <Box
        sx={{
          backgroundColor: cardBg,
          borderRadius: "20px",
          border: `1.5px solid ${cardBorder}`,
          p: { xs: 2.2, md: "20px 26px" },
          display: "flex",
          alignItems: "center",
          gap: 2,
          boxShadow: isDark ? "0 4px 20px rgba(0,0,0,0.25)" : "0 2px 10px rgba(10, 37, 64, 0.02)",
          mb: 3,
        }}
      >
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: "14px",
            backgroundColor: tealMain,
            color: "#FFFFFF",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <MedicalServicesOutlinedIcon sx={{ fontSize: "1.75rem" }} />
        </Box>

        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
            <Typography sx={{ fontSize: { xs: "1.2rem", md: "1.4rem" }, fontWeight: 800, color: primaryText }}>
              Clinical Report
            </Typography>
            <Chip
              label="Clinical Records"
              size="small"
              sx={{
                height: "22px",
                fontSize: "0.68rem",
                fontWeight: 700,
                backgroundColor: pastelTealBg,
                color: tealButton,
                borderRadius: "6px",
              }}
            />
          </Box>
          <Typography sx={{ fontSize: "0.82rem", color: secondaryText, fontWeight: 500, mt: 0.3 }}>
            Clinical encounter logs, assessments, and patient chart records
          </Typography>
        </Box>
      </Box>

      {/* 2. Filter Form */}
      <Box
        sx={{
          backgroundColor: cardBg,
          borderRadius: "20px",
          border: `1.5px solid ${cardBorder}`,
          p: { xs: 2.5, md: "26px 30px" },
          boxShadow: isDark ? "0 4px 20px rgba(0,0,0,0.25)" : "0 2px 10px rgba(10, 37, 64, 0.02)",
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
          <SearchIcon sx={{ color: labelTeal, fontSize: "1.3rem" }} />
          <Typography sx={{ fontSize: "1.05rem", fontWeight: 800, color: primaryText }}>
            Filter Clinical Records
          </Typography>
        </Box>

        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2.5 }}>
          {/* Patient Search */}
          <Box>
            <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, color: labelTeal, mb: 0.8 }}>Patient Name</Typography>
            <Select
              fullWidth
              displayEmpty
              size="small"
              value={patient}
              onChange={(e) => setPatient(e.target.value)}
              IconComponent={KeyboardArrowDownRoundedIcon}
              MenuProps={menuProps}
              renderValue={(selected) => (selected ? selected : <Typography sx={{ color: secondaryText, fontSize: "0.85rem" }}>Select patient...</Typography>)}
              sx={{ borderRadius: "8px", backgroundColor: controlBg, color: primaryText, fontSize: "0.85rem", "& fieldset": { borderColor: controlBorder } }}
            >
              <MenuItem value="">All Patients</MenuItem>
              <MenuItem value="Eleanor Vance">Eleanor Vance</MenuItem>
              <MenuItem value="Charlotte Bennett">Charlotte Bennett</MenuItem>
              <MenuItem value="Marcus Aurelius Vance">Marcus Aurelius Vance</MenuItem>
              <MenuItem value="Sophia Martinez">Sophia Martinez</MenuItem>
            </Select>
          </Box>

          {/* Staff Select */}
          <Box>
            <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, color: labelTeal, mb: 0.8 }}>Practitioner / Staff</Typography>
            <Select
              fullWidth
              displayEmpty
              size="small"
              value={staff}
              onChange={(e) => setStaff(e.target.value)}
              IconComponent={KeyboardArrowDownRoundedIcon}
              MenuProps={menuProps}
              renderValue={(selected) => (selected ? selected : <Typography sx={{ color: secondaryText, fontSize: "0.85rem" }}>Select staff...</Typography>)}
              sx={{ borderRadius: "8px", backgroundColor: controlBg, color: primaryText, fontSize: "0.85rem", "& fieldset": { borderColor: controlBorder } }}
            >
              <MenuItem value="">All Staff</MenuItem>
              <MenuItem value="Dr. Usman Rehman">Dr. Usman Rehman</MenuItem>
              <MenuItem value="Dr. Sarah Jenkins">Dr. Sarah Jenkins</MenuItem>
              <MenuItem value="Elena Rostova, RN">Elena Rostova, RN</MenuItem>
              <MenuItem value="Dr. Bilal">Dr. Bilal</MenuItem>
            </Select>
          </Box>

          {/* Date Range */}
          <Box>
            <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, color: labelTeal, mb: 0.8 }}>Encounter Date:</Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <TextField
                fullWidth
                size="small"
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px", backgroundColor: controlBg, color: primaryText, fontSize: "0.85rem", "& fieldset": { borderColor: controlBorder } } }}
              />
              <Typography sx={{ fontSize: "0.82rem", color: secondaryText, fontWeight: 600 }}>to</Typography>
              <TextField
                fullWidth
                size="small"
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px", backgroundColor: controlBg, color: primaryText, fontSize: "0.85rem", "& fieldset": { borderColor: controlBorder } } }}
              />
            </Box>
          </Box>
        </Box>

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, mt: 3.5 }}>
          <Button
            variant="contained"
            startIcon={<SearchIcon />}
            sx={{ height: "40px", backgroundColor: tealMain, color: "#FFFFFF", fontWeight: 700, fontSize: "0.84rem", borderRadius: "8px", px: 3, textTransform: "none", boxShadow: "none", "&:hover": { backgroundColor: tealButton } }}
          >
            Search
          </Button>
          <Button
            variant="outlined"
            onClick={handleClear}
            startIcon={<DeleteOutlineRoundedIcon />}
            sx={{ height: "40px", borderColor: tealButton, color: tealButton, fontWeight: 700, fontSize: "0.84rem", borderRadius: "8px", px: 2.8, textTransform: "none" }}
          >
            Clear
          </Button>
        </Box>
      </Box>

      {/* 3. Results Table */}
      <Box sx={{ backgroundColor: cardBg, borderRadius: "16px", border: `1px solid ${softCardBorder}`, overflow: "hidden" }}>
        <TableContainer sx={{ minHeight: "300px" }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow sx={{ "& th": { color: primaryText, fontWeight: 800, fontSize: "0.86rem", backgroundColor: isDark ? "#162238" : "#F8FAFC", py: 2, px: 3 } }}>
                <TableCell>Encounter ID</TableCell>
                <TableCell>Patient Name</TableCell>
                <TableCell>Staff Member</TableCell>
                <TableCell>Encounter Type</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} sx={{ py: 10, textAlign: "center" }}>
                    <Typography sx={{ color: secondaryText, fontSize: "0.9rem", fontWeight: 600 }}>No matching records found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => (
                  <TableRow key={row.id} sx={{ "&:hover": { backgroundColor: isDark ? "rgba(30, 94, 95, 0.08)" : "rgba(36, 94, 101, 0.03)" } }}>
                    <TableCell sx={{ fontWeight: 700, color: tealButton }}>{row.id}</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: primaryText }}>{row.patient}</TableCell>
                    <TableCell sx={{ color: primaryText }}>{row.staff}</TableCell>
                    <TableCell sx={{ color: secondaryText }}>{row.type}</TableCell>
                    <TableCell sx={{ color: secondaryText }}>{row.date}</TableCell>
                    <TableCell>
                      <Chip
                        label={row.status}
                        size="small"
                        sx={{
                          borderRadius: "6px",
                          fontWeight: 700,
                          backgroundColor: row.status === "Completed" ? (isDark ? "rgba(16,185,129,0.15)" : "#DCFCE7") : "#FEF3C7",
                          color: row.status === "Completed" ? "#15803D" : "#B45309",
                        }}
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredRows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          sx={{ borderTop: `1px solid ${softCardBorder}`, color: secondaryText }}
        />
      </Box>
    </Box>
  );
};

export default ClinicalReport;